import {
  BookUser,
  Bot,
  Clock,
  Cpu,
  Globe,
  Handshake,
  Headset,
  LayoutDashboard,
  MessageCircle,
  Mic2,
  Send,
  Settings,
  ShieldAlert,
  Tag,
  UserCog,
  UserCircle,
  Users,
  Volume2,
  Webhook,
  Aperture,
  Bell,
  Megaphone,
  ScrollText,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { DEFAULT_TTS_VOICE_ID, DEFAULT_VOICE_CHANGER_MODEL_ID } from '@wxproto-desk/shared'
import type { GroupAutoReplyConfig, GroupKeywordReplyRule, SettingsState } from '@/types'

export type MenuItem = {
  id: string
  path: string
  label: string
  icon: LucideIcon
  group?: string
  adminOnly?: boolean
  external?: boolean
  /** 未读徽标：数值由 SecondaryColumn 的 badges 按菜单项 id 下发。 */
  badge?: boolean
}

export type MenuKey = 'dashboard' | 'conversation' | 'message' | 'contact' | 'group' | 'settings'

const settingsItems: MenuItem[] = [
  { id: 'console-account', path: '/settings/console-account', label: '控制台账号', icon: UserCog, group: '控制台设置' },
  {
    id: 'console-users',
    path: '/settings/console-users',
    label: '账号管理',
    icon: Users,
    group: '控制台设置',
    adminOnly: true,
  },
  {
    id: 'announcements-admin',
    path: '/settings/announcements-admin',
    label: '公告管理',
    icon: ScrollText,
    group: '控制台设置',
    adminOnly: true,
  },
  { id: 'personal', path: '/settings/personal', label: '个人详情', icon: UserCircle, group: '个人设置' },
  { id: 'partner', path: '/settings/partner', label: '合伙人', icon: Handshake, group: '个人设置' },
  { id: 'system', path: '/settings/system', label: '系统设置', icon: Bell, group: '个人设置' },
  { id: 'voice-changer', path: '/settings/voice-changer', label: '变声器', icon: Mic2, group: '个人设置' },
  { id: 'tts-config', path: '/settings/tts-config', label: '文字转语音', icon: Volume2, group: '个人设置' },
  {
    id: 'webhook-trigger',
    path: '/settings/webhook-trigger',
    label: 'Webhook 触发器',
    icon: Webhook,
    group: '个人设置',
  },
  { id: 'ai-config', path: '/settings/ai-config', label: 'AI 大模型配置', icon: Cpu, group: '全局设置' },
  { id: 'announcements', path: '/settings/announcements', label: '公告', icon: Megaphone, group: '帮助', badge: true },
  // 未登录状态也要可见（不加入 menuItemsFor 的 hiddenWhenLoggedOut）。
  { id: 'support', path: '/settings/support', label: '联系客服', icon: Headset, group: '帮助' },
  // 外链：官网。external 项由 SecondaryColumn 渲染为新标签页链接，不走内部路由。
  { id: 'website', path: 'https://www.wxproto.cn', label: '访问官网', icon: Globe, group: '帮助', external: true },
]

export const menus: Record<MenuKey, { label: string; items: MenuItem[] }> = {
  dashboard: {
    label: '概览',
    items: [{ id: 'dashboard', path: '/dashboard', label: '控制台', icon: LayoutDashboard }, ...settingsItems],
  },
  conversation: {
    label: '对话',
    items: [{ id: 'realtime', path: '/conversation/realtime', label: '实时对话', icon: MessageCircle }],
  },
  message: {
    label: '自动化',
    items: [
      { id: 'automation', path: '/settings/automation', label: '自动化与安全机制', icon: Settings },
      { id: 'auto-reply', path: '/message/auto-reply', label: '自动回复', icon: Bot },
      { id: 'scheduled', path: '/message/scheduled', label: '定时发送消息', icon: Clock },
      { id: 'mass', path: '/message/mass', label: '群发消息', icon: Send },
      { id: 'moments', path: '/message/moments', label: '朋友圈助手', icon: Aperture },
    ],
  },
  contact: {
    label: '好友管理',
    items: [
      { id: 'address-book', path: '/contact/address-book', label: '通讯录', icon: BookUser },
      { id: 'labels', path: '/contact/labels', label: '标签管理', icon: Tag },
      { id: 'check-status', path: '/contact/check-status', label: '检测好友关系', icon: ShieldAlert },
    ],
  },
  group: {
    label: '群聊管理',
    items: [
      { id: 'welcome', path: '/group/welcome', label: '入群欢迎语', icon: MessageCircle },
      { id: 'group-auto-reply', path: '/group/auto-reply', label: '群自动回复', icon: Bot },
      { id: 'members', path: '/group/members', label: '群成员列表', icon: Users },
    ],
  },
  settings: {
    label: '设置',
    items: settingsItems,
  },
}

/**
 * Settings menu is almost identical whether or not the user is logged in —
 * only the "自动化与安全机制" item is hidden for logged-out sessions so the
 * list matches the reference WechatBotConsole layout.
 */
export function menuItemsFor(key: MenuKey, isLoggedIn: boolean, role?: string): MenuItem[] {
  const items = menus[key].items
  const roleFiltered = items.filter((item) => !item.adminOnly || role === 'admin')
  if (isLoggedIn) return roleFiltered
  // 仅隐藏真正依赖微信账号的菜单；合伙人/公告属于控制台账号维度，未绑微信号也可见。
  const hiddenWhenLoggedOut = ['automation', 'system', 'voice-changer', 'tts-config', 'webhook-trigger']
  if (key === 'settings' || key === 'dashboard') {
    return roleFiltered
      .filter((item) => !hiddenWhenLoggedOut.includes(item.id))
      .map((item) => (item.id === 'personal' ? { ...item, label: '账号登录' } : item))
  }
  return roleFiltered
}

export const emptySettings: SettingsState = {
  aiConfig: {
    apiBaseUrl: 'https://api.openai.com/v1',
    apiKey: '',
    model: 'gpt-4o-mini',
  },
  aiReply: {
    enabled: false,
    aiEnabled: false,
    keywordEnabled: true,
    prompt: '你现在是我的私人助理，请用自然、简洁、礼貌的方式代我回复消息。',
    contactWxids: [],
    contactLabelIds: [],
  },
  security: { autoAcceptFriend: false, replyDelaySeconds: 2 },
  system: { messageNotifications: false },
  voiceChanger: { enabled: false, selectedModelId: DEFAULT_VOICE_CHANGER_MODEL_ID },
  tts: { enabled: false, selectedVoiceId: DEFAULT_TTS_VOICE_ID },
  groupWelcome: {},
  groupAutoReply: {},
}

export const defaultGroupAutoReplyPrompt = '请根据群聊消息自然、简洁地回复。'

export function createGroupKeywordRule(): GroupKeywordReplyRule {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    enabled: true,
    keyword: '',
    reply: '',
    replyType: 'text',
    replyPayload: { type: 'text', content: '' },
    matchType: 'exact',
  }
}

export function normalizeGroupAutoReplyConfig(config?: Partial<GroupAutoReplyConfig> | null): GroupAutoReplyConfig {
  const keywordRules: GroupKeywordReplyRule[] =
    Array.isArray(config?.keywordRules) && config.keywordRules.length
      ? config.keywordRules.map((rule) => ({
          id: rule.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          enabled: rule.enabled !== false,
          keyword: rule.keyword || '',
          reply: rule.reply || '',
          replyType: rule.replyType || 'text',
          replyPayload: rule.replyPayload || { type: rule.replyType || 'text', content: rule.reply || '' },
          matchType: rule.matchType === 'fuzzy' || rule.matchType === 'regex' ? rule.matchType : 'exact',
        }))
      : config?.keyword || config?.reply
        ? [
            {
              id: 'legacy',
              enabled: true,
              keyword: config.keyword || '',
              reply: config.reply || '',
              replyType: 'text',
              replyPayload: { type: 'text', content: config.reply || '' },
              matchType: 'exact',
            },
          ]
        : []

  // Derive independent toggles with backward compatibility with the old
  // mutually-exclusive `mode` field.
  const aiEnabled = typeof config?.aiEnabled === 'boolean' ? config.aiEnabled : config?.mode === 'ai'
  const keywordEnabled =
    typeof config?.keywordEnabled === 'boolean' ? config.keywordEnabled : config?.mode === 'keyword' ? true : false

  return {
    enabled: config?.enabled ?? false,
    aiEnabled,
    keywordEnabled,
    keywordRules,
    prompt: config?.prompt || defaultGroupAutoReplyPrompt,
  }
}
